import { reportTypeStage, type DecisionStage, type ReportTypeId } from '../../config/reportTypes';
import type { SiteEntity } from '../../types/site';
import type { ProjectIntent } from '../../types/siteRag';
import {
  evaluateFinancialReportReadiness,
  evaluateGenerationImpactReportReadiness,
  evaluateReportAuthorityReadiness,
  evaluateSupplyChainReportReadiness,
} from '../reportGeneration/reportAuthorityReadiness';
import { assessGenerationImpactTestFitApplicability } from '../generationImpact/testFitApplicability';

/**
 * The three user-facing buckets used by Analysis surfaces.
 *
 * This is deliberately separate from lifecycle: a current required analysis is
 * still part of the run-now decision set, while an optional analysis can be
 * technically runnable without becoming decision work.
 */
export type AnalysisEligibilityGroup =
  | 'run-now'
  | 'available-later'
  | 'optional';

export type AnalysisApplicabilityReasonCode =
  | 'always-required'
  | 'intent-required'
  | 'intent-relevant'
  | 'authority-ready'
  | 'financial-model-missing'
  | 'financial-model-incomplete'
  | 'supply-chain-bom-missing'
  // STAGE 5.2 — `declared-load-missing` RETIRED as the Generation & Impact
  // gate. A declared load is still the denominator for firm-capacity adequacy,
  // but it is not what makes the report attributable: a promoted, solved
  // scenario is. The load check also mis-fired — it read `powerRequirementMW`
  // through the campus SSOT and still told a site to declare a load it had
  // declared as a RANGE bucket.
  | 'generation-scenario-missing'
  | 'generation-scenario-stale'
  | 'generation-not-applicable'
  | 'connectivity-advisory'
  | 'dependency-not-ready'
  /** Two-stage pipeline: a Stage 2 analysis before a generation scenario is promoted. */
  | 'stage-locked'
  | 'optional';

/**
 * The analyses that are part of the minimum decision basis for EVERY site, for
 * the `always-required` reason code. Mirrors `ALWAYS_REQUIRED_FOR_GO`
 * (`mandatoryReports.ts` — which imports this module, so the set is restated
 * here rather than imported; `analysisApplicability.test.ts` pins the mirror)
 * plus `power-availability`, which is load-gated rather than intent-gated and
 * therefore universal in the same sense. A required type outside this set is
 * labelled `intent-required`.
 */
export const UNCONDITIONAL_DECISION_BASIS: ReadonlySet<ReportTypeId> = new Set<ReportTypeId>([
  'feasibility',
  'power-availability',
  // `water-resource` left on 2026-09-07 (Phase 3 part B): retired; its screen
  // is part of feasibility, which is already here.
  // Two-stage Phase 3 (D1 + D2): environmental and financial for every site —
  // both Stage 2 · Mature validation since the owner's 2026-09-30 move of
  // environmental ("it requires equipment").
  'environmental',
  'financial',
]);

export type AnalysisApplicabilityRole =
  | 'required'
  | 'intent-relevant'
  | 'optional';

/** The site-owned authority needed to activate model-dependent analyses. */
export type AnalysisAuthoritySite = Partial<SiteEntity>;

export interface AnalysisDecisionMateriality {
  decisionMaterial: boolean;
  prerequisiteReady: boolean;
  reasonCode: AnalysisApplicabilityReasonCode;
  reason: string;
}

export interface AnalysisApplicability extends AnalysisDecisionMateriality {
  type: ReportTypeId;
  /**
   * Two-stage pipeline membership from the registry; null for the executive
   * summary and for an additional analysis outside both stages (Public Sentiment).
   */
  stage: DecisionStage | null;
  role: AnalysisApplicabilityRole;
  eligibilityGroup: AnalysisEligibilityGroup;
}

export interface ResolveAnalysisApplicabilityInput {
  type: ReportTypeId;
  site: AnalysisAuthoritySite;
  requiredTypes: readonly ReportTypeId[];
  scopeTypes: readonly ReportTypeId[];
  /**
   * The EFFECTIVE intent, when it is threaded separately from the site record
   * (Path-to-GO does exactly that). Must be the same value the required-set
   * calculation used, or the two engines will disagree about the same site.
   */
  intent?: ProjectIntent | null;
}

/**
 * Resolve whether a report domain is allowed to participate in a decision.
 *
 * Financial and Supply Chain are not merely missing reports: their source
 * models must exist before those domains have a defensible meaning. Keeping
 * this check here prevents Path-to-GO, portfolio scoring, rollups and automatic
 * refresh from each inventing a different approximation of "ready".
 */

/**
 * TWO-STAGE LOCK (owner decision 2, 2026-09-06) — a Stage 2 analysis other
 * than Generation & Impact (which carries its own, richer scenario reasons)
 * and Interconnection (advisory) is not decision-relevant until a generation
 * scenario is PROMOTED and still valid. A site that has DECLARED a grid-only
 * supply path can never promote one, so it is not locked: its Stage 2 work is
 * financial and procurement diligence on grid power. Applied at the
 * APPLICABILITY layer (plan lane, wizard) and explicitly at the server enqueue
 * chokepoint — never inside `resolveAnalysisDecisionMateriality`, which also
 * feeds the required-for-GO set and therefore the composite's source floor
 * (Phase 1 changes no scoring input). Both read this one function, so the
 * client never fabricates a block the server would not raise.
 *
 * ENVIRONMENTAL (owner, 2026-09-30: "move Environmental reports into Mature
 * validation, as it requires equipment"). The equipment the environmental
 * assessment needs is the PROMOTED GENERATION SCENARIO — the on-site
 * generation technology, nameplate and receptors behind its air-emissions
 * headroom, noise and generation-footprint findings. The report already
 * freezes that scenario (`GENERATION_IMPACT_REPORT_TYPES`) and its prompt
 * reads the scenario block, so it takes exactly this lock and nothing more:
 * NOT the equipment BOM (a procurement basis the environmental report never
 * reads — that gate stays Supply Chain's own `supply-chain-bom-missing`).
 * GRID-ONLY: a site that DECLARED a grid-only supply path has no on-site
 * equipment and can never promote a scenario, so — like financial — it is not
 * locked; its environmental clearance (NEPA/CEQA, wetlands, habitat, water,
 * cultural resources) runs as Stage 2 work on the grid-only basis and stays
 * required for GO. An UNDECLARED strategy is locked (it is told to declare or
 * promote), the same as every other Stage 2 analysis.
 */
const STAGE_LOCKED_TYPES: ReadonlySet<ReportTypeId> = new Set<ReportTypeId>([
  'environmental',
  'financial',
  'supply-chain-risk',
  'test-fit',
]);

export function stageLockFor(
  type: ReportTypeId,
  site: AnalysisAuthoritySite,
  effectiveIntent: ProjectIntent | null | undefined,
): AnalysisDecisionMateriality | null {
  if (!STAGE_LOCKED_TYPES.has(type) || reportTypeStage(type) !== 2) return null;
  const generation = evaluateGenerationImpactReportReadiness({
    ...site,
    projectIntent: effectiveIntent ?? undefined,
    promotedGenerationScenarioSnapshot: site.promotedGenerationScenarioSnapshot,
    typedPreferencesDefaults: site.typedPreferencesDefaults,
  });
  if (generation.ready && !(type === 'test-fit' && site.generationAssessmentSelection?.kind === 'grid-led-standby-planning')) return null;
  if (generation.blockers[0]?.code === 'generation-not-applicable') return null;
  return {
    decisionMaterial: false,
    prerequisiteReady: false,
    reasonCode: 'stage-locked',
    reason:
      (site.generationAssessmentSelection?.kind === 'grid-led-standby-planning' && type === 'test-fit'
        ? 'Static planning does not qualify a physical Test Fit. Select a qualified engineering/layout assessment. '
        : 'Stage 2 (Mature validation) unlocks once a generation assessment is selected or a solved scenario is promoted. ')
      + 'Solve and promote a scenario in the simulation editor first.',
  };
}
export function resolveAnalysisDecisionMateriality(
  type: ReportTypeId,
  site: AnalysisAuthoritySite,
  /**
   * The EFFECTIVE project intent, when the caller holds one that may differ
   * from `site.projectIntent` — Path-to-GO threads intent separately from the
   * site record. Defaults to the site's own intent.
   *
   * This matters because the required-analysis set reads intent directly while
   * applicability reads the site: if the two diverge, one engine calls Power
   * required while the other calls it later work, and the surfaces disagree
   * about the same site.
   */
  intentOverride?: ProjectIntent | null,
): AnalysisDecisionMateriality {
  const effectiveIntent = intentOverride ?? site.projectIntent;
  if (type === 'interconnection') {
    return {
      decisionMaterial: false,
      prerequisiteReady: true,
      reasonCode: 'connectivity-advisory',
      reason:
        'Connectivity is an optional advisory analysis and does not change decision readiness.',
    };
  }

  // A selected static profile supports declared planning, not a physical
  // equipment layout. Materiality must retain the same boundary as the
  // runtime/enqueue authority guard; an inactive v1 promotion is no fallback.
  if (type === 'test-fit' && site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') {
    const readiness = evaluateReportAuthorityReadiness(type, { ...site, projectIntent: effectiveIntent });
    return {
      decisionMaterial: false,
      prerequisiteReady: false,
      reasonCode: 'dependency-not-ready',
      reason: readiness.blockers[0]!.message,
    };
  }

  if (type === 'financial') {
    const readiness = evaluateFinancialReportReadiness(site.financialAssumptions);
    if (!readiness.ready) {
      const blocker = readiness.blockers[0];
      return {
        decisionMaterial: false,
        prerequisiteReady: false,
        reasonCode:
          blocker?.code === 'financial-model-incomplete'
            ? 'financial-model-incomplete'
            : 'financial-model-missing',
        reason:
          blocker?.message
          ?? 'Add and save the minimum project financial model before Financial analysis becomes decision-relevant.',
      };
    }
    return {
      decisionMaterial: true,
      prerequisiteReady: true,
      reasonCode: 'authority-ready',
      reason:
        'The saved project financial model is complete enough to support Financial analysis.',
    };
  }

  if (type === 'power-availability') {
    // STAGE 5.2 — this is the Generation & Impact Report (the id stays
    // `power-availability`; only the label changed — see 5.5). Its grid
    // SCREENING half now lives in BTM Feasibility, so what remains is the power
    // consequence of a specific solved generation scenario: firm-capacity
    // adequacy, technology comparison, LCOE, grid-vs-BTM.
    //
    // GRID-ONLY sites: the report does not APPLY, it is not blocked. Such a
    // site will never promote a scenario, so an "available later" lane would
    // dangle a prerequisite it is never meant to satisfy. All the grid analysis
    // it needs now reaches it through feasibility, which is why 5.1 had to ship
    // first. `prerequisiteReady: true` keeps it out of the blocked lane and
    // lets it fall through to `optional`.
    const generationApplicability = assessGenerationImpactTestFitApplicability({
      projectIntent: effectiveIntent,
      powerAvailabilityPreferences: site.typedPreferencesDefaults?.['power-availability'],
    });
    if (generationApplicability.status === 'grid-only' && site.generationAssessmentSelection?.kind !== 'grid-led-standby-planning') {
      return {
        decisionMaterial: false,
        prerequisiteReady: true,
        reasonCode: 'generation-not-applicable',
        reason:
          'This site declares a grid-only supply path, so Generation & Impact does not apply. '
          + 'Grid capacity, interconnection queue, tariffs and curtailment are covered by the Early Screening Assessment.',
      };
    }
    // Otherwise the gate is a PROMOTED scenario, exactly as Financial is gated
    // on a saved model. A draft never sets the promotion stamp, so having
    // solved something is not enough — the site must have chosen the basis the
    // LCOE and grid-vs-BTM conclusions will be attributed to.
    // EFFECTIVE intent, not `site.projectIntent`. Path-to-GO threads intent
    // separately from the site record, and this function's own contract says
    // the two engines must read the same value or they will disagree about the
    // same site — one calling the report required while the other calls it
    // later work.
    //
    // `typedPreferencesDefaults` MUST be passed. Readiness re-derives
    // applicability internally, and `btmStrategy` is the only signal that
    // distinguishes a HYBRID or BTM-ONLY site whose phases happen to carry
    // `powerSource: 'grid'` from a genuinely grid-only one. Omitting it here
    // (while the sibling call above passes it) made readiness answer
    // "grid-only" for such a site, so this function reported it blocked with
    // the grid-only text under a `generation-scenario-missing` code, while
    // every caller that passes the FULL site — the runtime assert and the
    // retry router — saw it as ready. That is the mirror image of the
    // disagreement this gate was added to remove, and it is reachable without
    // any hand-editing: promoting a grid-import-only scenario writes
    // `powerSource: 'grid'` onto the phase.
    const readiness = evaluateGenerationImpactReportReadiness({
      ...site,
      projectIntent: effectiveIntent,
      promotedGenerationScenarioSnapshot: site.promotedGenerationScenarioSnapshot,
      typedPreferencesDefaults: site.typedPreferencesDefaults,
    });
    if (!readiness.ready) {
      const blocker = readiness.blockers[0];
      return {
        decisionMaterial: false,
        prerequisiteReady: false,
        reasonCode:
          blocker?.code === 'generation-scenario-stale'
            ? 'generation-scenario-stale'
            : 'generation-scenario-missing',
        reason:
          blocker?.message
          ?? 'Promote a solved generation scenario before Generation & Impact becomes decision-relevant.',
      };
    }
    return {
      decisionMaterial: true,
      prerequisiteReady: true,
      reasonCode: 'authority-ready',
      reason:
        site.generationAssessmentSelection?.kind === 'grid-led-standby-planning'
          ? 'The selected static planning assessment provides declared grid/standby capacity and source-factor accounting; engineering remains indeterminate.'
          : 'A promoted generation scenario gives firm-capacity adequacy, LCOE and the grid-vs-BTM '
            + 'comparison a solved basis to be measured against.',
    };
  }

  if (type === 'supply-chain-risk') {
    const readiness = evaluateSupplyChainReportReadiness(site.supplyChainPlan);
    if (!readiness.ready) {
      return {
        decisionMaterial: false,
        prerequisiteReady: false,
        reasonCode: 'supply-chain-bom-missing',
        reason:
          readiness.blockers[0]?.message
          ?? 'Choose the equipment basis and add a valid BOM item before Supply Chain Risk becomes decision-relevant.',
      };
    }
    return {
      decisionMaterial: true,
      prerequisiteReady: true,
      reasonCode: 'authority-ready',
      reason:
        'The saved equipment BOM is specific enough to support Supply Chain Risk analysis.',
    };
  }

  return {
    decisionMaterial: true,
    prerequisiteReady: true,
    reasonCode: 'optional',
    reason: 'This analysis is available when it is useful to the project.',
  };
}

/**
 * Resolve the Path-to-GO role after applying the site-owned prerequisite gate.
 * A caller may pass a broad intent scope; this function is the fail-closed
 * boundary that prevents Interconnection or an authority-less Financial/SCM
 * analysis from being promoted by that broad scope.
 */
export function resolveAnalysisApplicability(
  input: ResolveAnalysisApplicabilityInput,
): AnalysisApplicability {
  return { ...resolveAnalysisApplicabilityCore(input), stage: reportTypeStage(input.type) };
}

function resolveAnalysisApplicabilityCore(
  input: ResolveAnalysisApplicabilityInput,
): Omit<AnalysisApplicability, 'stage'> {
  const { type, site, requiredTypes, scopeTypes, intent } = input;
  const materiality = resolveAnalysisDecisionMateriality(type, site, intent);
  const requestedAsRequired = requiredTypes.includes(type);
  const requestedAsRelevant = scopeTypes.includes(type);

  // The dormant connectivity re-promotion seam is represented by explicitly
  // placing Interconnection in the required set. Production policy does not do
  // that today; without that explicit signal it always remains advisory.
  if (type === 'interconnection' && requestedAsRequired) {
    return {
      type,
      role: 'required',
      eligibilityGroup: 'run-now',
      decisionMaterial: true,
      prerequisiteReady: true,
      reasonCode: 'intent-required',
      reason: 'Connectivity is required under the active decision policy.',
    };
  }

  if (type === 'interconnection') {
    return {
      type,
      role: 'optional',
      eligibilityGroup: 'optional',
      ...materiality,
    };
  }

  // PUBLIC SENTIMENT is an ADDITIONAL analysis (owner, 2026-09-30: "move
  // Public Sentiment into Additional analyses"). It is outside both stages
  // (no registry stage) and never in the required-for-GO set, so neither a
  // broad scope that names it — the rollup scopes every PRESENT report type —
  // nor a caller's required list may promote it to decision work: that would
  // list it as stage-less decision work instead of under "Additional
  // analyses". Unlike Connectivity there is no policy flag that could re-gate
  // it. Its materiality is untouched, so a saved report still feeds the
  // composite's social pillar exactly as any present optional report does.
  if (type === 'public-sentiment') {
    return {
      type,
      role: 'optional',
      eligibilityGroup: 'optional',
      ...materiality,
      reasonCode: 'optional',
      reason:
        'Public Sentiment is an additional analysis. It does not change either stage\'s required analyses, '
        + 'the screening result or the screening score.',
    };
  }

  // TWO-STAGE LOCK — the role is what the decision policy says (a required
  // Stage 2 analysis stays required for GO); the lane says it cannot run yet.
  // An analysis whose OWN input is still missing (Financial without a model,
  // Supply Chain without an equipment basis) reports THAT reason instead: the
  // reader can prepare it now, while the scenario is solved — the lane is the
  // same either way.
  const stageLock = stageLockFor(type, site, intent ?? site.projectIntent);
  if (stageLock && materiality.prerequisiteReady) {
    return {
      type,
      role: requestedAsRequired ? 'required' : requestedAsRelevant ? 'intent-relevant' : 'optional',
      eligibilityGroup: 'available-later',
      decisionMaterial: materiality.decisionMaterial,
      prerequisiteReady: false,
      reasonCode: stageLock.reasonCode,
      reason: stageLock.reason,
    };
  }
  if (!materiality.prerequisiteReady) {
    // The role is what the decision policy says — a REQUIRED Stage 2 analysis
    // (financial without its model, supply chain without its equipment basis)
    // stays required for GO; the lane says it cannot run yet. Reading it as
    // optional here made Path to GO › Analyse say "Decision work is current"
    // while the composite hold was withholding the band for exactly it.
    return {
      type,
      role: requestedAsRequired ? 'required' : requestedAsRelevant ? 'intent-relevant' : 'optional',
      eligibilityGroup: 'available-later',
      ...materiality,
    };
  }

  if (requestedAsRequired) {
    return {
      type,
      role: 'required',
      eligibilityGroup: 'run-now',
      ...materiality,
      reasonCode:
        UNCONDITIONAL_DECISION_BASIS.has(type)
          ? 'always-required'
          : 'intent-required',
      reason:
        UNCONDITIONAL_DECISION_BASIS.has(type)
          ? 'This analysis is part of the minimum decision basis for every site.'
          : 'The declared project intent makes this analysis part of the current decision basis.',
    };
  }

  // Completing a financial model is itself an explicit act that makes Financial
  // relevant, even when a broader intent signal did not make it mandatory: the
  // model has a high, unambiguous completeness bar (see
  // `evaluateFinancialReportReadiness`), so saving one is a deliberate decision.
  //
  // Supply Chain deliberately does NOT get that bypass. An equipment basis can
  // accumulate incidentally while a team explores components, so possessing one
  // is not by itself a declaration that procurement risk is current decision
  // work. It must ALSO be signalled by intent — which matches the required-set
  // rule in `requiredReportsForGoUnderPolicy`, where SCM needs intent AND
  // authority. Without this, the Analysis surface promoted Supply Chain to
  // run-now while the GO gate did not require it: two rules, one screen.
  // A ready-but-unsignalled plan still falls through to `optional` below, so
  // the analysis stays runnable — it just stops claiming to be decision work.
  if (requestedAsRelevant || type === 'financial') {
    return {
      type,
      role: 'intent-relevant',
      eligibilityGroup: 'run-now',
      ...materiality,
      reasonCode:
        type === 'financial' || type === 'supply-chain-risk'
          ? 'authority-ready'
          : 'intent-relevant',
      reason:
        type === 'financial' || type === 'supply-chain-risk'
          ? materiality.reason
          : 'The declared project intent makes this analysis relevant, but it is not required for the decision gate.',
    };
  }

  return {
    type,
    role: 'optional',
    eligibilityGroup: 'optional',
    ...materiality,
    reasonCode: 'optional',
    reason: 'Optional deep dive; it does not block the current decision path.',
  };
}

/** Stable activation token used by composite fingerprints and tests. */
export function analysisDecisionMaterialityToken(
  site: AnalysisAuthoritySite,
): string {
  const financial = resolveAnalysisDecisionMateriality('financial', site);
  const supplyChain = resolveAnalysisDecisionMateriality('supply-chain-risk', site);
  return `financial:${financial.decisionMaterial ? '1' : '0'}|supply-chain:${supplyChain.decisionMaterial ? '1' : '0'}|interconnection:0`;
}
